import * as Logger from "./logger.js";
import { HSComponent } from "./component.js";
import { UserSession } from './session.js';
import { hasCapability } from './capabilities.js';
import styles from "./header.css";
import template from "./header.html";
import { HSRoundedButton } from "./rounded-button.js";
import { HSSelectBox } from "./selectbox.js";

class HSHeader extends HSComponent {
    statusRefreshTimer = null;

    constructor() {
        super();
    }

    async onLoad() {
        this.loadTemplate(template);
        this.loadStyles(styles);
        this.setupMenuHandlers();
        this.startOpsStatusMonitor();
    }

    onUnload() {
        if (this.statusRefreshTimer) {
            window.clearInterval(this.statusRefreshTimer);
            this.statusRefreshTimer = null;
        }
        super.onUnload();
    }

    startOpsStatusMonitor() {
        this.onEvent('hs-sse-status', () => this.renderOpsStatus(), window);
        this.onEvent('hs-version-status', () => { this.renderOpsStatus(); this.renderVersionInfo(); }, window);
        this.statusRefreshTimer = window.setInterval(() => this.renderOpsStatus(), 5000);
        this.renderOpsStatus();
    }

    resolveOpsStatusState() {
        const versionStatus = UserSession.get('appVersionStatus') || {};
        const status = UserSession.get('sseStatus') || {};
        const phase = status.phase || '';
        const hasStream = !!UserSession.get('sseStreamId');
        const activeSubscriptions = Object.keys(UserSession.get('sseSubscriptions') || {}).length;

        if (versionStatus.state === 'stale') {
            return {
                state: 'stale-version',
                label: 'Update',
                message: versionStatus.message || 'A newer dashboard version is available. Click to reload.',
            };
        }

        // Reserved future state for backend/frontend version mismatch signaling.
        if (phase === 'stale-version') {
            return {
                state: 'stale-version',
                label: 'Update Required',
                message: status.message || 'Frontend code is out of date. Please reload this page.',
            };
        }

        if (phase === 'alert') {
            return {
                state: 'alert',
                label: 'Alert',
                message: status.message || 'SSE connection failed. Please reload the page.',
            };
        }

        if (phase === 'idle') {
            return {
                state: 'idle',
                label: 'Idle',
                message: status.message || 'SSE idle — no active live subscriptions',
            };
        }

        if (phase === 'loading') {
            return {
                state: 'loading',
                label: 'Loading',
                message: status.message || 'Connecting SSE...',
            };
        }

        if (hasStream && activeSubscriptions > 0) {
            return {
                state: 'live',
                label: 'Live',
                message: `${activeSubscriptions} active subscription${activeSubscriptions === 1 ? '' : 's'}`,
            };
        }

        if (hasStream) {
            return {
                state: 'steady',
                label: 'Steady',
                message: 'SSE connected, no active live subscriptions',
            };
        }

        return {
            state: 'loading',
            label: 'Loading',
            message: status.message || 'Waiting for SSE session...',
        };
    }

    renderVersionInfo() {
        const userInfo = UserSession.get('userinfo');
        const showVersion = userInfo?.usertype === 'ops' || userInfo?.usertype === 'developer' || userInfo?.usertype === 'devops';
        const versionEl = this.shadowRoot.querySelector('#menu-version');
        if (!versionEl) return;
        if (!showVersion) {
            versionEl.classList.add('hide');
            return;
        }
        const version = UserSession.get('appVersionBaseline')?.version;
        versionEl.textContent = version ? `v${version}` : '';
        versionEl.classList.toggle('hide', !version);
    }

    renderOpsStatus() {
        const userInfo = UserSession.get('userinfo');
        const canSeeLiveStatus = hasCapability(userInfo, 'live.logs.read');
        const showVersionUpdate = UserSession.get('appVersionStatus')?.state === 'stale';
        const badge = this.shadowRoot.querySelector('#menu-status');
        const label = this.shadowRoot.querySelector('#menu-status-text');

        if (!badge || !label) {
            return;
        }

        if (!canSeeLiveStatus && !showVersionUpdate) {
            badge.classList.add('hide');
            return;
        }

        const next = this.resolveOpsStatusState();
        badge.classList.remove('hide', 'state-loading', 'state-live', 'state-steady', 'state-idle', 'state-alert', 'state-stale-version');
        badge.classList.add(`state-${next.state}`);
        badge.title = next.message || '';
        label.textContent = `Status: ${next.label}`;
    }

    setupMenuHandlers() {
        const menuButton = this.shadowRoot.querySelector('#menu-button');
        const menuDropdown = this.shadowRoot.querySelector('#menu-dropdown');
        const menuMyDashboard = this.shadowRoot.querySelector('#menu-my-dashboard');
        const menuDefaultDashboard = this.shadowRoot.querySelector('#menu-default-dashboard');
        const menuEditMode = this.shadowRoot.querySelector('#menu-edit-mode');
        const menuStatus = this.shadowRoot.querySelector('#menu-status');
        const menuExplore = this.shadowRoot.querySelector('#menu-explore');
        const menuLiveLogs = this.shadowRoot.querySelector('#menu-live-logs');
        const menuPipelineSandbox = this.shadowRoot.querySelector('#menu-pipeline-sandbox');
        const menuServiceMonitor = this.shadowRoot.querySelector('#menu-service-monitor');
        const menuUpgrade = this.shadowRoot.querySelector('#menu-upgrade');

        this.onEvent('click', () => {
            closeMenu();
            this.emit({ type: 'hs-open-explorer' });
        }, menuExplore);

        this.onEvent('click', () => {
            closeMenu();
            this.emit({ type: 'hs-open-log-viewer' });
        }, menuLiveLogs);

        this.onEvent('click', () => {
            closeMenu();
            this.emit({ type: 'hs-open-pipeline-sandbox' });
        }, menuPipelineSandbox);

        this.onEvent('click', () => {
            closeMenu();
            this.emit({ type: 'hs-open-service-monitor' });
        }, menuServiceMonitor);

        this.onEvent('click', () => {
            const next = this.resolveOpsStatusState();
            if (next.state === 'stale-version') {
                window.location.reload();
            }
        }, menuStatus);
        const menuSendFeedback = this.shadowRoot.querySelector('#menu-send-feedback');
        const menuResetDashboard = this.shadowRoot.querySelector('#menu-reset-dashboard');
        const feedbackModal = this.shadowRoot.querySelector('#feedback-modal');
        const feedbackBackdrop = this.shadowRoot.querySelector('#feedback-backdrop');
        const feedbackClose = this.shadowRoot.querySelector('#feedback-close');
        const feedbackCancel = this.shadowRoot.querySelector('#feedback-cancel');
        const feedbackSubmit = this.shadowRoot.querySelector('#feedback-submit');
        const feedbackCategory = this.shadowRoot.querySelector('#feedback-category');
        const feedbackTitle = this.shadowRoot.querySelector('#feedback-title-input');
        const feedbackMessage = this.shadowRoot.querySelector('#feedback-message');
        const feedbackStatus = this.shadowRoot.querySelector('#feedback-status');

        if (!menuButton || !menuDropdown) {
            return;
        }

        this.onEvent('click', () => {
            const opening = menuDropdown.classList.contains('hide');
            menuDropdown.classList.toggle('hide', !opening);
            menuButton.setAttribute('aria-expanded', String(opening));
        }, menuButton);

        const closeMenu = () => {
            menuDropdown.classList.add('hide');
            menuButton.setAttribute('aria-expanded', 'false');
        };

        const openFeedbackModal = () => {
            if (!feedbackModal) { return; }
            feedbackStatus.textContent = '';
            feedbackStatus.classList.remove('success', 'error');
            feedbackModal.classList.remove('hide');
            feedbackMessage?.focus();
        };

        const closeFeedbackModal = () => {
            if (!feedbackModal) { return; }
            feedbackModal.classList.add('hide');
        };

        this.onEvent('click', () => {
            closeMenu();
            this.emit({ type: 'hs-load-dashboard-custom' });
        }, menuMyDashboard);

        this.onEvent('click', () => {
            closeMenu();
            this.emit({ type: 'hs-load-dashboard-default' });
        }, menuDefaultDashboard);

        this.onEvent('click', () => {
            closeMenu();
            this.emit({ type: 'hs-toggle-edit-mode' });
        }, menuEditMode);

        this.onEvent('click', () => {
            closeMenu();
            openFeedbackModal();
        }, menuSendFeedback);

        this.onEvent('click', () => {
            closeMenu();
            this.emit({ type: 'hs-reset-dashboard' });
        }, menuResetDashboard);

        this.onEvent('click', () => closeFeedbackModal(), feedbackBackdrop);
        this.onEvent('click', () => closeFeedbackModal(), feedbackClose);
        this.onEvent('click', () => closeFeedbackModal(), feedbackCancel);

        this.onEvent('click', async () => {
            const payload = {
                category: feedbackCategory?.value || '',
                title: feedbackTitle?.value || '',
                message: feedbackMessage?.value || '',
                page: window.location.pathname,
                meta: {
                    dashboard_view: UserSession.get('dashboard_view') || '',
                    selected_orgs: (UserSession.get('selected_orgs') || []).map(x => x?.id || x),
                    selected_providers: (UserSession.get('selected_providers') || []).map(x => x?.id || x),
                },
            };

            if (!payload.message.trim()) {
                feedbackStatus.textContent = 'Please enter feedback before submitting.';
                feedbackStatus.classList.remove('success');
                feedbackStatus.classList.add('error');
                return;
            }

            feedbackSubmit.disabled = true;
            feedbackStatus.textContent = 'Submitting feedback...';
            feedbackStatus.classList.remove('success', 'error');
            try {
                await this.fetch('/api/feedback', { method: 'POST', payload });
                feedbackStatus.textContent = 'Feedback submitted. Thank you.';
                feedbackStatus.classList.add('success');
                feedbackTitle.value = '';
                feedbackMessage.value = '';
                setTimeout(() => closeFeedbackModal(), 800);
            } catch (err) {
                feedbackStatus.textContent = err?.message || 'Failed to submit feedback.';
                feedbackStatus.classList.add('error');
                Logger.warn('feedback submit failed: ', err);
            } finally {
                feedbackSubmit.disabled = false;
            }
        }, feedbackSubmit);

        // Upgrade modal
        const upgradeModal = this.shadowRoot.querySelector('#upgrade-modal');
        const upgradeBackdrop = this.shadowRoot.querySelector('#upgrade-backdrop');
        const upgradeClose = this.shadowRoot.querySelector('#upgrade-close');
        const upgradeCancel = this.shadowRoot.querySelector('#upgrade-cancel');
        const upgradeSubmit = this.shadowRoot.querySelector('#upgrade-submit');
        const upgradeBranch = this.shadowRoot.querySelector('#upgrade-branch');
        const upgradeBranchCustom = this.shadowRoot.querySelector('#upgrade-branch-custom');
        const upgradeBranchToggle = this.shadowRoot.querySelector('#upgrade-branch-toggle');

        const upgradeStatus = this.shadowRoot.querySelector('#upgrade-status');
        const upgradeLog = this.shadowRoot.querySelector('#upgrade-log');
        const upgradeInfo = this.shadowRoot.querySelector('#upgrade-current-info');
        let upgradeCustomMode = false;
        let _upgradeBranchShaMap = {};
        let _upgradeCurrentSha = null;

        const openUpgradeModal = async () => {
            if (!upgradeModal) return;
            upgradeStatus.textContent = '';
            upgradeStatus.classList.remove('success', 'error');
            upgradeLog.textContent = '';
            upgradeLog.classList.add('hide');
            upgradeSubmit.disabled = false;
            upgradeModal.classList.remove('hide');

            _upgradeCurrentSha = null;
            _upgradeBranchShaMap = {};

            // Fetch current status
            try {
                const status = await this.fetch('/api/upgrade/status');
                _upgradeCurrentSha = status.git_sha || null;
                const currentRef = status.git_ref || null;
                let info = `Image: ${status.current_image || 'unknown'}`;
                if (currentRef) info += `\nBranch: ${currentRef}`;
                if (_upgradeCurrentSha) info += `\nSHA: ${_upgradeCurrentSha.slice(0, 12)}`;
                if (status.build_time) info += `\nBuilt: ${status.build_time}`;
                upgradeInfo.textContent = info;
            } catch {
                upgradeInfo.textContent = 'Could not fetch current service info.';
            }

            // Fetch branches
            upgradeBranch.innerHTML = '<option disabled selected>Loading branches…</option>';
            upgradeBranch.disabled = true;
            upgradeSubmit.disabled = true;
            try {
                const data = await this.fetch('/api/upgrade/branches');
                upgradeBranch.innerHTML = '';
                if (data.branches?.length) {
                    data.branches.forEach(b => {
                        _upgradeBranchShaMap[b.ref] = b.sha;
                        const opt = document.createElement('option');
                        opt.value = b.ref;
                        const isCurrent = _upgradeCurrentSha && b.sha === _upgradeCurrentSha;
                        opt.textContent = isCurrent ? `${b.ref} (current)` : b.ref;
                        upgradeBranch.appendChild(opt);
                    });
                } else {
                    upgradeBranch.innerHTML = '<option>main</option>';
                }
            } catch {
                upgradeBranch.innerHTML = '<option>main</option>';
            }
            upgradeBranch.disabled = false;
            upgradeSubmit.disabled = false;

            const checkAlreadyLatest = () => {
                if (!_upgradeCurrentSha || upgradeCustomMode) return;
                const selectedRef = upgradeBranch?.value;
                const selectedSha = _upgradeBranchShaMap[selectedRef];
                if (selectedSha && selectedSha === _upgradeCurrentSha) {
                    upgradeStatus.textContent = 'Already running the latest commit for this branch.';
                    upgradeStatus.classList.add('success');
                } else {
                    upgradeStatus.textContent = '';
                    upgradeStatus.classList.remove('success', 'error');
                }
            };
            checkAlreadyLatest();
            if (upgradeBranch) {
                this.onEvent('change', checkAlreadyLatest, upgradeBranch);
            }
        };

        const closeUpgradeModal = () => {
            if (upgradeModal) upgradeModal.classList.add('hide');
        };

        if (menuUpgrade) {
            this.onEvent('click', () => {
                closeMenu();
                openUpgradeModal();
            }, menuUpgrade);
        }

        this.onEvent('click', () => closeUpgradeModal(), upgradeBackdrop);
        this.onEvent('click', () => closeUpgradeModal(), upgradeClose);
        this.onEvent('click', () => closeUpgradeModal(), upgradeCancel);

        if (upgradeBranchToggle) {
            this.onEvent('click', () => {
                upgradeCustomMode = !upgradeCustomMode;
                upgradeBranch.classList.toggle('hide', upgradeCustomMode);
                upgradeBranchCustom.classList.toggle('hide', !upgradeCustomMode);
                upgradeBranchToggle.classList.toggle('active', upgradeCustomMode);
            }, upgradeBranchToggle);
        }

        const waitForRestart = () => {
            let attempts = 0;
            upgradeStatus.textContent = 'Service restarting...';
            upgradeStatus.classList.remove('success', 'error');
            const check = async () => {
                attempts++;
                try {
                    const controller = new AbortController();
                    const timer = setTimeout(() => controller.abort(), 5000);
                    const resp = await fetch('/api/version', { signal: controller.signal });
                    clearTimeout(timer);
                    if (resp.ok && attempts > 2) {
                        upgradeStatus.textContent = 'Service is back up. Reloading...';
                        upgradeStatus.classList.add('success');
                        setTimeout(() => location.reload(), 1500);
                        return;
                    }
                    setTimeout(check, 3000);
                } catch {
                    upgradeStatus.textContent = 'Service is restarting — waiting for it to come back...';
                    setTimeout(check, 4000);
                }
            };
            setTimeout(check, 5000);
        };

        const pollBuildStatus = async () => {
            const poll = async () => {
                try {
                    const status = await this.fetch('/api/upgrade/build/status');
                    if (status.log?.length) {
                        upgradeLog.classList.remove('hide');
                        upgradeLog.textContent = status.log.join('\n');
                        upgradeLog.scrollTop = upgradeLog.scrollHeight;
                    }
                    if (status.state === 'complete') {
                        upgradeStatus.textContent = 'Build and deploy complete. Service restarting...';
                        upgradeStatus.classList.add('success');
                        waitForRestart();
                        return;
                    }
                    if (status.state === 'failed') {
                        upgradeStatus.textContent = `Failed: ${status.error || 'unknown error'}`;
                        upgradeStatus.classList.add('error');
                        upgradeSubmit.disabled = false;
                        return;
                    }
                    upgradeStatus.textContent = `Status: ${status.state}...`;
                    setTimeout(poll, 2000);
                } catch {
                    upgradeStatus.textContent = 'Lost connection — service may be restarting...';
                    waitForRestart();
                }
            };
            setTimeout(poll, 1500);
        };

        if (upgradeSubmit) {
            this.onEvent('click', async () => {
                const ref = upgradeCustomMode
                    ? (upgradeBranchCustom?.value || '').trim()
                    : (upgradeBranch?.value || '').trim();

                if (!ref) {
                    upgradeStatus.textContent = 'Please select or enter a branch name.';
                    upgradeStatus.classList.add('error');
                    return;
                }

                upgradeSubmit.disabled = true;
                upgradeStatus.textContent = 'Starting build and deploy...';
                upgradeStatus.classList.remove('success', 'error');
                upgradeLog.textContent = '';
                upgradeLog.classList.remove('hide');

                try {
                    await this.fetch('/api/upgrade/build-and-deploy', {
                        method: 'POST',
                        payload: { ref },
                    });
                } catch (err) {
                    upgradeStatus.textContent = err?.message || 'Failed to start build.';
                    upgradeStatus.classList.add('error');
                    upgradeSubmit.disabled = false;
                    return;
                }
                upgradeStatus.textContent = 'Build started — polling for status...';
                pollBuildStatus();
            }, upgradeSubmit);
        }

        this.onEvent('click', (_, ev) => {
            if (!ev.composedPath().includes(this)) {
                closeMenu();
            }
        }, document);

        this.onEvent('keydown', (_, ev) => {
            if (ev.key === 'Escape') {
                closeFeedbackModal();
                closeUpgradeModal();
                closeMenu();
            }
        }, document);
    }

    init(config = {}) {
        super.init(config);
        const userInfo = UserSession.get("userinfo");
        const formatUserType = (info) => {
            if (!info?.usertype) {
                return 'Unknown';
            }
            if (info.usertype === 'developer') {
                return info.admin === true ? 'developer-admin' : 'developer';
            }
            if (info.usertype === 'devops') {
                return info.admin === true ? 'devops-admin' : 'devops';
            }
            if (info.usertype === 'ops') {
                return info.admin === true ? 'ops-admin' : 'ops';
            }
            if (info.usertype === 'super') {
                return info.admin === true ? 'super-admin' : 'super';
            }
            if (info.usertype === 'org') {
                return info.admin === true ? 'org-admin' : 'org';
            }
            if (info.usertype === 'dashboard') {
                return info.admin === true ? 'dashboard-admin' : 'dashboard';
            }
            if (info.usertype === 'ml') {
                return info.admin === true ? 'ml-admin' : 'ml';
            }
            return info.usertype;
        };
        // Live-logs users: status badge + live logs. Developer/devops: explore + dashboard controls (*-admin only: edit/reset).
        const isOps = userInfo?.usertype === 'ops';
        const isDeveloper = userInfo?.usertype === 'developer';
        const isDevops = userInfo?.usertype === 'devops';
        const canReadLiveLogs = hasCapability(userInfo, 'live.logs.read');
        const canUseDockerLiveLogs = hasCapability(userInfo, 'live.logs.mode.docker');
        const canUseKafkaLiveLogs = hasCapability(userInfo, 'live.logs.mode.kafka');
        const canOpenLiveLogs = canReadLiveLogs && (canUseDockerLiveLogs || canUseKafkaLiveLogs);
        const canEdit = (isDeveloper || isDevops) && userInfo?.admin === true;
        const showVersionUpdate = UserSession.get('appVersionStatus')?.state === 'stale';
        const dashboardView = UserSession.get('dashboard_view') || 'default';
        const editMode = !!UserSession.get('edit_mode');

        const menuWrapper = this.shadowRoot.querySelector('#header-menu-wrapper');
        const menuMyDashboard = this.shadowRoot.querySelector('#menu-my-dashboard');
        const menuDefaultDashboard = this.shadowRoot.querySelector('#menu-default-dashboard');
        const menuEditMode = this.shadowRoot.querySelector('#menu-edit-mode');
        const menuResetDashboard = this.shadowRoot.querySelector('#menu-reset-dashboard');
        const menuStatus = this.shadowRoot.querySelector('#menu-status');
        const menuExplore = this.shadowRoot.querySelector('#menu-explore');
        const menuLiveLogs = this.shadowRoot.querySelector('#menu-live-logs');
        const menuPipelineSandbox = this.shadowRoot.querySelector('#menu-pipeline-sandbox');
        const menuServiceMonitor = this.shadowRoot.querySelector('#menu-service-monitor');
        const menuUpgrade = this.shadowRoot.querySelector('#menu-upgrade');
        const menuUserName = this.shadowRoot.querySelector('#menu-user-name');
        const menuUserType = this.shadowRoot.querySelector('#menu-user-type');

        if (menuUserName) {
            menuUserName.textContent = userInfo?.preferred_username
                || userInfo?.name
                || userInfo?.email
                || 'Unknown user';
        }
        if (menuUserType) {
            menuUserType.textContent = `User Type: ${formatUserType(userInfo)}`;
        }

        this.renderVersionInfo();

        this.classList.toggle('ops-advanced', canOpenLiveLogs || isDeveloper || isDevops);

        menuStatus?.classList.add('hide');
        menuExplore?.classList.add('hide');
        menuLiveLogs?.classList.add('hide');
        menuPipelineSandbox?.classList.add('hide');
        menuServiceMonitor?.classList.add('hide');
        menuMyDashboard?.classList.add('hide');
        menuDefaultDashboard?.classList.add('hide');
        menuEditMode?.classList.add('hide');
        menuResetDashboard?.classList.add('hide');
        menuUpgrade?.classList.add('hide');

        if (isDeveloper || isDevops) {
            // developer/devops: explore + live logs; *-admin also gets dashboard mutation controls
            menuWrapper?.classList.remove('hide');
            menuExplore?.classList.remove('hide');
            menuLiveLogs?.classList.remove('hide');
            if (hasCapability(userInfo, 'elm.sandbox.access')) {
                menuPipelineSandbox?.classList.remove('hide');
            }
            if (hasCapability(userInfo, 'elm.services.access')) {
                menuServiceMonitor?.classList.remove('hide');
            }
            Logger.info('upgrade menu check:', {
                isDeveloper, isDevops,
                hasUpgradeCap: hasCapability(userInfo, 'service.upgrade'),
                capabilities: userInfo?.capabilities,
                menuUpgradeFound: !!menuUpgrade,
            });
            if (hasCapability(userInfo, 'service.upgrade')) {
                menuUpgrade?.classList.remove('hide');
            }

            if (canEdit) {
                menuMyDashboard?.classList.remove('hide');
                menuDefaultDashboard?.classList.remove('hide');
                menuResetDashboard?.classList.remove('hide');
                if (dashboardView === 'custom') {
                    menuEditMode?.classList.remove('hide');
                } else {
                    menuEditMode?.classList.add('hide');
                }
            }

            menuEditMode?.classList.toggle('selected', editMode);
            menuMyDashboard?.classList.toggle('selected', dashboardView === 'custom');
            menuDefaultDashboard?.classList.toggle('selected', dashboardView === 'default');
        } else if (canOpenLiveLogs || userInfo?.usertype === 'ml') {
            // users with live logs capabilities or ml role get settings menu with live logs.
            menuWrapper?.classList.remove('hide');
            menuStatus?.classList.remove('hide');
            menuLiveLogs?.classList.remove('hide');
            if (hasCapability(userInfo, 'elm.services.access')) {
                menuServiceMonitor?.classList.remove('hide');
            }
            this.renderOpsStatus();
        } else {
            if (showVersionUpdate) {
                menuWrapper?.classList.remove('hide');
                menuStatus?.classList.remove('hide');
            } else {
                menuWrapper?.classList.add('hide');
            }
            this.renderOpsStatus();
        }

        //
        // build the crumbbar with either static text or dropdowns
        //
        const clouds = UserSession.get("clouds");
        const orgs = UserSession.get("orgs");
        const providers = UserSession.get("providers");
        const crumbbar = this.shadowRoot.querySelector(".crumbbar");

        if (providers.length > 0) {
            // show export data button
            this.shadowRoot.querySelector("#export-data-button").classList.remove('hide');
        }

        if (clouds.length > 1) {
            // cloud select, org select and provider select
            const cloudSelect = document.createElement("hs-selectbox");
            if (canEdit) { cloudSelect.classList.add('compact'); }
            crumbbar.appendChild(cloudSelect);
            cloudSelect.init({
                src: {
                    list: { type: "session", key: "filtered_clouds" },
                    selection: { type: "session", key: "selected_clouds" }
                },
                placeholder: 'Local Cloud',
                //placeholder: 'All Clouds',
                action: 'hs-cloud-select' 
            });
            const orgSelect = document.createElement("hs-selectbox");
            if (canEdit) { orgSelect.classList.add('compact'); }
            crumbbar.appendChild(orgSelect);
            orgSelect.init({
                src: {
                    list: { type: "session", key: "filtered_orgs" },
                    selection: { type: "session", key: "selected_orgs" }
                },
                placeholder: 'All Orgs',
                action: 'hs-org-select' 
            });
            const providerSelect = document.createElement("hs-selectbox");
            if (canEdit) { providerSelect.classList.add('compact'); }
            crumbbar.appendChild(providerSelect);
            providerSelect.init({ 
                src: {
                    list: { type: "session", key: "filtered_providers" },
                    selection: { type: "session", key: "selected_providers" }
                },
                placeholder: 'All Providers',
                action: 'hs-provider-select' 
            });
        } else {
            if (providers.length > 1) {
                if (orgs.length > 1) {
                    // org select and provider select
                    const orgSelect = document.createElement("hs-selectbox");
                    if (canEdit) { orgSelect.classList.add('compact'); }
                    crumbbar.appendChild(orgSelect);
                    orgSelect.init({
                        src: {
                            list: { type: "session", key: "filtered_orgs" },
                            selection: { type: "session", key: "selected_orgs" }
                        },
                        placeholder: 'All Orgs',
                        action: 'hs-org-select' 
                    });
                } else {
                    // org static name and provider select
                    const container = document.createElement("div");
                    container.classList.add("crumbbar-fixed-text-container");
                    crumbbar.appendChild(container);
                    const text = document.createElement("div");
                    text.classList.add("crumbbar-fixed-text");
                    text.textContent = orgs[0].name;
                    container.appendChild(text);
                    UserSession.set("selected_orgs", orgs);
                }
                const providerSelect = document.createElement("hs-selectbox");
                if (canEdit) { providerSelect.classList.add('compact'); }
                crumbbar.appendChild(providerSelect);
                providerSelect.init({ 
                    src: {
                        list: { type: "session", key: "filtered_providers" },
                        selection: { type: "session", key: "selected_providers" }
                    },
                    placeholder: 'All Providers',
                    action: 'hs-provider-select' 
                });
            } else if (providers.length == 1) {
                const container = document.createElement("div");
                container.classList.add("crumbbar-fixed-text-container");
                crumbbar.appendChild(container);
                const text = document.createElement("div");
                text.classList.add("crumbbar-fixed-text");
                text.textContent = providers[0].name;
                container.appendChild(text);
                UserSession.set("selected_providers", providers);
            } else {
                Logger.warn("No providers");
            }
        }
    }

    async reloadData() {
        // tell all components we manage in our own shadowRoot DOM to reload their data
        const components = this.findChildren({
            predicate: (child) => child instanceof HSComponent,
            shadowOnly: true,
        });
        for (const component of components) {
            if (typeof component.reloadData === "function") {
                component.reloadData();
            }
        }
    }
}
customElements.define("hs-header", HSHeader);
